/**
 * PWA-UPDATE-1 — "is the user in the middle of something?"
 *
 * WHY THIS IS AN EXPLICIT REGISTRY AND NOT A HEURISTIC.
 * The obvious implementation is to scan the DOM for dirty inputs and to ask TanStack Query whether a
 * mutation is in flight. Both were tried on paper and both are wrong here:
 *
 *   - A DOM scan comparing `value` to `defaultValue` does not work with React. react-dom's
 *     `updateWrapper` calls `setDefaultValue` on every render, so a controlled text input with 500
 *     characters typed into it scans CLEAN. Meanwhile `defaultChecked` is frozen at mount, so a
 *     controlled checkbox scans DIRTY forever. The heuristic is not conservative — it is arbitrary in
 *     both directions, which is worse than having no signal, because it looks like one.
 *
 *   - `queryClient.isMutating()` only sees `useMutation`. Several of the most destructive screens in
 *     this app write with a bare `api.post` and hold their state in `useState` — bulk import's
 *     reviewed dry-run, the promotion preview, a teacher's swiped attendance. Those are invisible to
 *     it, and they are precisely the ones where a reload costs the most work.
 *
 * So screens declare it. A screen that knows it is holding something the user cannot cheaply recreate
 * claims a key; the update driver refuses to reload while any claim is held. `isMutating()` is still
 * OR'd in by the driver as a second, cheaper net — this registry replaces the DOM scan, not the query
 * signal.
 *
 * The registry is a Set of string keys rather than a counter so that two screens claiming the same
 * key (the credential-reveal screens, which are mutually exclusive) cannot leak a count.
 */

const claims = new Set<string>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      /* a broken listener must never stop the others, or a stuck claim becomes permanent */
    }
  }
}

/** Declare that this screen is holding work a reload would destroy. */
export function claimBusy(key: string): void {
  if (claims.has(key)) return;
  claims.add(key);
  notify();
}

/** Release the claim. Safe to call for a key that was never claimed. */
export function releaseBusy(key: string): void {
  if (!claims.delete(key)) return;
  notify();
}

export function isBusyClaimed(): boolean {
  return claims.size > 0;
}

/** Subscribe to claim/release. Returns the unsubscribe function. */
export function onBusyChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Read the live claim set. Used by the headless probe to assert the gate actually engaged. */
export function debugClaims(): string[] {
  return Array.from(claims);
}

/**
 * Exposed on `window` so the update behaviour can be exercised from outside the bundle.
 *
 * This is how `server/scripts/pwaupdate1-probe.mjs` puts a real production build into the busy state
 * and then releases it — the alternative would be driving a real screen through a full data-entry
 * flow in a headless browser, which would test the screen rather than the gate. It ships in prod
 * because the probe runs a production build (there is no service worker in dev, so a dev build could
 * not test any of this). Read-only inspection plus two setters on a Set of strings; nothing here
 * touches data or auth.
 */
try {
  (window as unknown as { __aisistooBusyDebug?: unknown }).__aisistooBusyDebug = {
    claim: claimBusy,
    release: releaseBusy,
    claims: debugClaims,
  };
} catch {
  /* non-browser context (SSR, tests) — nothing to expose */
}
